import { defineChatViewActions } from './actionsContext'
import { ChatPermissions } from '../../services'
import { isDialogChat, isGroupChat } from '../../types/ChatMessage'
import { useCurrentUser } from '@/modules/chat-matrix-fast/composables/useCurrentUser'
import { useUserStore } from '@/modules/user/stores/userStore'
import {
  getCurrentMatrixUserId,
  isMatrixRoomAdmin,
} from '@/modules/chat-matrix-fast/services/matrixChatClient'
import { useChatStore as useChatStoreCore } from '../chatStore'
import type { IChat } from '@/modules/chat-matrix-fast/types/IChat'

export const permissionActions = defineChatViewActions({
    getGroupAdminContext(): { matrixUserId: string | null; matrixRoomAdmin: boolean } {
      const chat = this.activeChat?.chat
      const matrixUserId = getCurrentMatrixUserId()
      const matrixRoomId = chat && isGroupChat(chat)
        ? (chat as IChat).matrix_room_id
        : undefined

      return {
        matrixUserId,
        matrixRoomAdmin: Boolean(matrixRoomId && isMatrixRoomAdmin(matrixRoomId)),
      }
    },

    isCurrentUserAdmin(): boolean {
      if (!this.activeChat?.chat) {
        return false
      }

      const capabilities = (this.activeChat.chat as IChat).capabilities
      if (capabilities?.can_manage_members != null) {
        return capabilities.can_manage_members
      }

      const currentUser = useCurrentUser()
      const adminContext = this.getGroupAdminContext()

      return ChatPermissions.isCurrentUserAdmin(
        this.activeChat.chat,
        currentUser?.id?.value,
        adminContext,
      )
    },

    isCurrentUserCreator(): boolean {
      if (!this.activeChat?.chat) {
        return false
      }

      const chat = this.activeChat.chat

      if (!isGroupChat(chat)) {
        return false
      }

      const userStore = useUserStore()
      const user = userStore.user

      const currentUserId = user?.id?.toString() ?? null
      const currentUserUuid = user?.uuid?.toString() ?? null
      const currentUserIdentifier = currentUserUuid || currentUserId

      if (!currentUserIdentifier) {
        return false
      }

      const id = String(currentUserIdentifier).trim()

      if (chat.owner && String(chat.owner).trim() === id) {
        return true
      }

      const matrixUserId = getCurrentMatrixUserId()
      if (matrixUserId && chat.owner && String(chat.owner).trim() === matrixUserId) {
        return true
      }

      if (chat.created_by) {
        const creatorId = chat.created_by.id || (chat.created_by as any).uuid
        if (creatorId && String(creatorId).trim() === id) {
          return true
        }
      }

      return false
    },

    isGroupOrChannel(): boolean {
      return ChatPermissions.isGroupOrChannel(this.activeChat?.chat)
    },

    isEventChat(): boolean {
      return ChatPermissions.isEventChat(this.activeChat?.chat)
    },

    canEditActiveChat(): boolean {
      return ChatPermissions.canEditChat(this.activeChat?.chat)
    },

    canManageChatSettings(): boolean {
      if (!this.activeChat?.chat) {
        return false
      }

      if (!this.isCurrentUserAdmin()) {
        return false
      }

      const capabilities = (this.activeChat.chat as IChat).capabilities
      if (capabilities?.can_edit === false) {
        return false
      }

      const currentUser = useCurrentUser()
      const adminContext = this.getGroupAdminContext()

      return ChatPermissions.canManageChatSettings(
        this.activeChat.chat,
        currentUser?.id?.value,
        adminContext,
      )
    },

    /** Право удалить группу/канал целиком (backend capabilities.can_delete) */

    canDeleteActiveChat(): boolean {
      const chat = this.activeChat?.chat
      if (!chat || !ChatPermissions.isGroupOrChannel(chat)) {
        return false
      }

      const capabilities = (chat as IChat).capabilities
      if (capabilities?.can_delete != null) {
        return capabilities.can_delete
      }

      return this.canManageChatSettings()
    },

    /** Право покинуть группу (backend запрещает выход создателю) */

    canLeaveActiveChat(): boolean {
      const chat = this.activeChat?.chat
      if (!chat || !ChatPermissions.isGroupOrChannel(chat)) {
        return false
      }

      const capabilities = (chat as IChat).capabilities
      if (capabilities?.can_leave != null) {
        return capabilities.can_leave
      }

      return !this.isCurrentUserCreator()
    },

    canPinMessages(): boolean {
      const chat = this.activeChat?.chat
      if (!chat) {
        return false
      }

      if (isDialogChat(chat)) {
        return true
      }

      return this.isCurrentUserAdmin()
    },

    canForwardFromActiveChat(): boolean {
      const viewChat = this.activeChat?.chat as IChat | null | undefined
      const coreChat = useChatStoreCore().currentChat
      const chat = coreChat ?? viewChat
      if (!chat) {
        return false
      }

      if (chat.capabilities?.can_forward_from != null) {
        return chat.capabilities.can_forward_from
      }

      const type = chat.type
      return type !== 'event' && type !== 'workgroup' && type !== 'channel'
    },
})
